import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #autoGiftSyncInProgress = false;
  #autoGiftResyncRequested = false;
  #autoGiftSource = 'cart-drawer-auto-gift';
  #autoGiftFlagProperty = '_cart_drawer_auto_gift';
  #autoGiftTierProperty = '_cart_drawer_gift_tier';

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleAutoGiftCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#syncAutoGifts);
    this.addEventListener('click', this.#handleUpsellClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleAutoGiftCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#syncAutoGifts);
    this.removeEventListener('click', this.#handleUpsellClick);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#scheduleAutoGiftSync();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Handles upsell add-to-cart actions from within the drawer.
   * @param {MouseEvent} event
   */
  #handleUpsellClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-cart-drawer-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    event.preventDefault();

    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    this.#setUpsellError(button, '');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const sectionIds = new Set();
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      cartItemsComponents.forEach((component) => {
        if (!(component instanceof HTMLElement) || !component.dataset.sectionId) return;
        sectionIds.add(component.dataset.sectionId);
      });

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('sections', Array.from(sectionIds).join(','));
      formData.append('sections_url', window.location.pathname);

      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'text/html',
        },
      });

      const data = await response.json();

      if (data.status) {
        this.#setUpsellError(button, data.message || 'Impossibile aggiungere il prodotto.');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'product-form-component',
          itemCount: 1,
          productId: button.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#setUpsellError(button, 'Impossibile aggiungere il prodotto. Riprova.');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  /**
   * Shows or clears the upsell error message for a specific card.
   * @param {HTMLButtonElement} button
   * @param {string} message
   */
  #setUpsellError(button, message) {
    const card = button.closest('.cart-drawer-upsell__card');
    if (!(card instanceof HTMLElement)) return;

    const errorElement = card.querySelector('[data-upsell-error]');
    if (!(errorElement instanceof HTMLElement)) return;

    errorElement.textContent = message;
    errorElement.classList.toggle('hidden', message === '');
  }

  #syncAutoGifts = () => {
    this.#scheduleAutoGiftSync();
  };

  /**
   * Syncs threshold gift lines when cart updates from any source.
   * @param {Event} event
   */
  #handleAutoGiftCartUpdate = (event) => {
    const source = /** @type {{ detail?: { data?: { source?: string } } }} */ (event).detail?.data?.source;
    if (source === this.#autoGiftSource) return;

    this.#scheduleAutoGiftSync();
  };

  #scheduleAutoGiftSync() {
    if (this.#autoGiftSyncInProgress) {
      this.#autoGiftResyncRequested = true;
      return;
    }

    void this.#runAutoGiftSync();
  }

  async #runAutoGiftSync() {
    this.#autoGiftSyncInProgress = true;

    try {
      let safetyCounter = 0;

      while (safetyCounter < 12) {
        safetyCounter += 1;

        const giftRulesConfig = this.#getAutoGiftRules();
        const cart = await this.#fetchCartState();

        if (!giftRulesConfig.enabled || giftRulesConfig.rules.length === 0) {
          const managedLines = (cart.items || []).filter((item) => this.#isAutoGiftLine(item));
          if (managedLines.length === 0) {
            return;
          }

          await this.#applyAutoGiftOperation({
            type: 'change',
            lineKey: managedLines[0].key,
            quantity: 0,
          });
          continue;
        }

        const nextOperation = this.#getNextAutoGiftOperation(cart, giftRulesConfig.rules);
        if (!nextOperation) {
          return;
        }

        await this.#applyAutoGiftOperation(nextOperation);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.#autoGiftSyncInProgress = false;

      if (this.#autoGiftResyncRequested) {
        this.#autoGiftResyncRequested = false;
        this.#scheduleAutoGiftSync();
      }
    }
  }

  /**
   * Reads auto-gift rules from drawer config script.
   * @returns {{ enabled: boolean, rules: Array<{ tierIndex: number, thresholdCents: number, variantId: number }> }}
   */
  #getAutoGiftRules() {
    const configElement = this.querySelector('[data-cart-drawer-auto-gift-config]');
    if (!(configElement instanceof HTMLScriptElement)) {
      return { enabled: false, rules: [] };
    }

    const isEnabled = configElement.dataset.enabled === 'true';
    if (!isEnabled) {
      return { enabled: false, rules: [] };
    }

    try {
      const parsed = JSON.parse(configElement.textContent || '[]');
      if (!Array.isArray(parsed)) {
        return { enabled: true, rules: [] };
      }

      const rules = parsed
        .map((rule) => ({
          tierIndex: Number(rule?.tierIndex),
          thresholdCents: Number(rule?.thresholdCents),
          variantId: Number(rule?.variantId),
        }))
        .filter((rule) => Number.isInteger(rule.tierIndex) && rule.tierIndex > 0)
        .filter((rule) => Number.isFinite(rule.thresholdCents) && rule.thresholdCents > 0)
        .filter((rule) => Number.isInteger(rule.variantId) && rule.variantId > 0)
        .sort((a, b) => a.thresholdCents - b.thresholdCents || a.tierIndex - b.tierIndex);

      return { enabled: true, rules };
    } catch (error) {
      console.error(error);
      return { enabled: true, rules: [] };
    }
  }

  /**
   * Fetches current cart JSON state.
   * @returns {Promise<{ total_price: number, items: Array<{ key: string, variant_id: number, quantity: number, line_price?: number, final_line_price?: number, properties?: Record<string, string | null> }> }>}
   */
  async #fetchCartState() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart state: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Computes the next add/change operation to enforce gift tier state.
   * @param {{ total_price: number, items: Array<{ key: string, variant_id: number, quantity: number, line_price?: number, final_line_price?: number, properties?: Record<string, string | null> }> }} cart
   * @param {Array<{ tierIndex: number, thresholdCents: number, variantId: number }>} rules
   * @returns {null | { type: 'add', rule: { tierIndex: number, thresholdCents: number, variantId: number } } | { type: 'change', lineKey: string, quantity: number }}
   */
  #getNextAutoGiftOperation(cart, rules) {
    const rulesByTier = new Map(rules.map((rule) => [rule.tierIndex, rule]));
    const qualifyingTotalPrice = (cart.items || []).reduce((total, item) => {
      if (this.#isAutoGiftLine(item)) {
        return total;
      }

      const linePrice = Number(item.final_line_price ?? item.line_price ?? 0);
      if (!Number.isFinite(linePrice) || linePrice < 0) {
        return total;
      }

      return total + linePrice;
    }, 0);

    /** @type {Map<number, Array<{ key: string, variant_id: number, quantity: number, properties?: Record<string, string | null> }>>} */
    const managedLinesByTier = new Map();
    /** @type {Array<{ key: string, variant_id: number, quantity: number, properties?: Record<string, string | null> }>} */
    const orphanManagedLines = [];

    for (const item of cart.items || []) {
      if (!this.#isAutoGiftLine(item)) continue;

      const tierIndex = Number(item.properties?.[this.#autoGiftTierProperty]);
      if (!Number.isInteger(tierIndex) || !rulesByTier.has(tierIndex)) {
        orphanManagedLines.push(item);
        continue;
      }

      const tierLines = managedLinesByTier.get(tierIndex) || [];
      tierLines.push(item);
      managedLinesByTier.set(tierIndex, tierLines);
    }

    if (orphanManagedLines.length > 0) {
      return {
        type: 'change',
        lineKey: orphanManagedLines[0].key,
        quantity: 0,
      };
    }

    for (const rule of rules) {
      const tierLines = managedLinesByTier.get(rule.tierIndex) || [];
      const isEligible = qualifyingTotalPrice >= rule.thresholdCents;

      if (!isEligible) {
        if (tierLines.length > 0) {
          return {
            type: 'change',
            lineKey: tierLines[0].key,
            quantity: 0,
          };
        }
        continue;
      }

      const matchingVariantLines = tierLines.filter((line) => Number(line.variant_id) === rule.variantId);
      const mismatchedVariantLines = tierLines.filter((line) => Number(line.variant_id) !== rule.variantId);

      if (mismatchedVariantLines.length > 0) {
        return {
          type: 'change',
          lineKey: mismatchedVariantLines[0].key,
          quantity: 0,
        };
      }

      if (matchingVariantLines.length > 1) {
        return {
          type: 'change',
          lineKey: matchingVariantLines[1].key,
          quantity: 0,
        };
      }

      if (matchingVariantLines.length === 0) {
        return {
          type: 'add',
          rule,
        };
      }

      if (Number(matchingVariantLines[0].quantity) !== 1) {
        return {
          type: 'change',
          lineKey: matchingVariantLines[0].key,
          quantity: 1,
        };
      }
    }

    return null;
  }

  /**
   * Applies one auto-gift operation and dispatches a cart update event for section morphing.
   * @param {{ type: 'add', rule: { tierIndex: number, thresholdCents: number, variantId: number } } | { type: 'change', lineKey: string, quantity: number }} operation
   */
  async #applyAutoGiftOperation(operation) {
    const sectionIds = this.#buildCartSectionsPayload();

    if (operation.type === 'add') {
      const formData = new FormData();
      formData.append('id', operation.rule.variantId.toString());
      formData.append('quantity', '1');
      formData.append(`properties[${this.#autoGiftFlagProperty}]`, 'true');
      formData.append(`properties[${this.#autoGiftTierProperty}]`, operation.rule.tierIndex.toString());
      formData.append('sections', sectionIds);
      formData.append('sections_url', window.location.pathname);

      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/json',
        },
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data?.message || 'Failed to add threshold gift item');
      }

      this.#dispatchAutoGiftCartUpdate(data);
      return;
    }

    const formData = new FormData();
    formData.append('id', operation.lineKey);
    formData.append('quantity', operation.quantity.toString());
    formData.append('sections', sectionIds);
    formData.append('sections_url', window.location.pathname);

    const config = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_change_url, {
      ...config,
      headers: {
        ...config.headers,
        Accept: 'application/json',
      },
    });
    const data = await response.json();

    if (!response.ok || data.errors) {
      throw new Error(data?.message || 'Failed to update threshold gift item');
    }

    this.#dispatchAutoGiftCartUpdate(data);
  }

  #buildCartSectionsPayload() {
    const sectionIds = new Set();
    const cartItemsComponents = document.querySelectorAll('cart-items-component');

    cartItemsComponents.forEach((component) => {
      if (!(component instanceof HTMLElement) || !component.dataset.sectionId) return;
      sectionIds.add(component.dataset.sectionId);
    });

    return Array.from(sectionIds).join(',');
  }

  /**
   * Dispatches a cart update event after an auto-gift mutation.
   * @param {{ sections?: Record<string, string>, item_count?: number }} data
   */
  #dispatchAutoGiftCartUpdate(data) {
    this.dispatchEvent(
      new CartUpdateEvent(data, this.#autoGiftSource, {
        source: this.#autoGiftSource,
        itemCount: data.item_count,
        sections: data.sections,
      })
    );
  }

  /**
   * @param {{ properties?: Record<string, string | null> }} lineItem
   */
  #isAutoGiftLine(lineItem) {
    return lineItem?.properties?.[this.#autoGiftFlagProperty] === 'true';
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
